import { useForm } from 'react-hook-form'
import {
  FormControl,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import {
  Button,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  ModalCloseButton,
  useToast,
} from '@opengovsg/design-system-react'

import { ISSUE_REPORT_DESCRIPTION_MAX_LEN } from '~shared/constants'
import { FEATURE_FLAGS } from '~shared/constants'
import { SubmitIssueReportReq } from '~shared/dto'
import { isAsciiLocalpartEmail } from '~shared/utils/validation'

import { getNetworkErrorMessage } from '~lib/api'
import {
  getPrefilledFeedbackFormUrl,
  PUBLIC_FEEDBACK_FORMSG_LINK,
  PUBLIC_FEEDBACK_FORMSG_URL_FIELD_ID,
} from '~constants/config'
import { ButtonStack } from '~components/ButtonStack'

import { useSubmitIssueReport } from '~features/form/hooks/useSubmitIssueReport'

export interface ReportIssueModalProps {
  isOpen: boolean
  onClose: () => void
  eventId: string
}

type ReportIssueFormFields = {
  description: string
  contactEmail: string
}

export const ReportIssueModal = ({
  isOpen,
  onClose,
  eventId,
}: ReportIssueModalProps) => {
  const toast = useToast()
  const { mutate, isLoading } = useSubmitIssueReport({ eventId })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReportIssueFormFields>({
    defaultValues: { description: '', contactEmail: '' },
  })

  const handleModalClose = () => {
    onClose()
    reset()
  }

  const onSubmit = handleSubmit(({ description, contactEmail }) => {
    const body: SubmitIssueReportReq = {
      description,
      // The contact email is optional, so don't send an empty string
      contactEmail: contactEmail || undefined,
    }
    mutate(body, {
      onSuccess: () => {
        toast({
          status: 'success',
          description: 'Thank you for reporting this issue.',
        })
        handleModalClose()
      },
      onError: (error) => {
        toast({
          status: 'error',
          description: getNetworkErrorMessage(error),
        })
      },
    })
  })

  const isPlatformTabEnabled = useFeatureIsOn(
    FEATURE_FLAGS.REPORT_ISSUE_PLATFORM_TAB,
  )

  const reportIssueForm = (
    <form noValidate onSubmit={onSubmit}>
      <VStack spacing="1rem" align="stretch">
        <Text textStyle="body-2">
          Report an issue with this event, e.g. incorrect details. Your report
          goes to the event organiser.
        </Text>
        <FormControl isRequired isInvalid={!!errors.description}>
          <FormLabel>Description of the issue</FormLabel>
          <Textarea
            {...register('description', {
              required: 'Please describe the issue',
              maxLength: {
                value: ISSUE_REPORT_DESCRIPTION_MAX_LEN,
                message:
                  'Please keep the description to 2,000 characters or less',
              },
            })}
          />
          <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors.contactEmail}>
          <FormLabel
            description="Leave your email if you are open to being contacted about this issue"
            isRequired={false}
          >
            Contact email (optional)
          </FormLabel>
          <Input
            type="email"
            placeholder="me@example.com"
            {...register('contactEmail', {
              validate: (value) =>
                !value ||
                isAsciiLocalpartEmail(value) ||
                'Please enter a valid email. Special characters are not allowed.',
            })}
          />
          <FormErrorMessage>{errors.contactEmail?.message}</FormErrorMessage>
        </FormControl>
        <ButtonStack
          isReverseOrderOnMobile
          alignSelf={{ base: 'stretch', md: 'flex-end' }}
        >
          <Button
            variant="clear"
            colorScheme="secondary"
            onClick={handleModalClose}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading} isDisabled={isLoading}>
            Submit
          </Button>
        </ButtonStack>
      </VStack>
    </form>
  )

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose}>
      <ModalOverlay />
      <ModalContent my={{ base: 0, md: 8 }}>
        <ModalCloseButton />
        <ModalHeader>Report an issue</ModalHeader>
        <ModalBody pb="2rem">
          {isPlatformTabEnabled ? (
            <Tabs variant="line">
              <TabList>
                <Tab>This event</Tab>
                <Tab>The CalSG platform</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0} pt="1rem">
                  {reportIssueForm}
                </TabPanel>
                <TabPanel px={0} pt="1rem">
                  <VStack spacing="1rem" align="stretch">
                    <Text textStyle="body-2">
                      If your issue is with the CalSG platform itself, e.g. the
                      page is not loading or you are unable to submit a booking,
                      contact the CalSG team instead. Issues with the platform
                      are not handled by the event organiser.
                    </Text>
                    <Link
                      href={getPrefilledFeedbackFormUrl({
                        formLink: PUBLIC_FEEDBACK_FORMSG_LINK,
                        urlFieldId: PUBLIC_FEEDBACK_FORMSG_URL_FIELD_ID,
                      })}
                      isExternal
                    >
                      Contact the CalSG team
                    </Link>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            reportIssueForm
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
